import * as THREE from "three";
import {
  CLASSES,
  COMPANIONS,
  ENEMIES,
  REGIONS,
  STATUS_DEFINITIONS,
  WORLD_SIZE,
} from "./config.js";

const QUALITY = Object.freeze({
  low: {
    ratio: 1,
    shadows: false,
    shadowMap: 512,
    vegetation: 34,
    particles: 36,
  },
  medium: {
    ratio: 1.35,
    shadows: true,
    shadowMap: 1024,
    vegetation: 62,
    particles: 72,
  },
  high: {
    ratio: 1.75,
    shadows: true,
    shadowMap: 1536,
    vegetation: 92,
    particles: 110,
  },
  ultra: {
    ratio: 2,
    shadows: true,
    shadowMap: 2048,
    vegetation: 128,
    particles: 160,
  },
});

const ANIMATIONS = Object.freeze([
  "idle",
  "walk",
  "run",
  "basic-attack",
  "secondary-attack",
  "cast",
  "channel",
  "dodge",
  "block",
  "hit",
  "stun",
  "death",
  "respawn",
  "victory",
  "interaction",
]);

function disposeMaterial(resource, disposedMaterials, disposedTextures) {
  if (!resource || disposedMaterials.has(resource)) return;
  disposedMaterials.add(resource);
  for (const value of Object.values(resource)) {
    if (
      value?.isTexture &&
      !value.userData?.realmfallShared &&
      !disposedTextures.has(value)
    ) {
      disposedTextures.add(value);
      value.dispose();
    }
  }
  resource.dispose?.();
}

function disposeObject(object) {
  const disposedGeometries = new Set();
  const disposedMaterials = new Set();
  const disposedTextures = new Set();
  object.traverse((child) => {
    if (
      child.geometry &&
      !child.geometry.userData?.realmfallShared &&
      !disposedGeometries.has(child.geometry)
    ) {
      disposedGeometries.add(child.geometry);
      child.geometry.dispose();
    }
    if (Array.isArray(child.material))
      child.material.forEach((entry) =>
        disposeMaterial(entry, disposedMaterials, disposedTextures),
      );
    else disposeMaterial(child.material, disposedMaterials, disposedTextures);
  });
}

function material(
  color,
  roughness = 0.78,
  metalness = 0.05,
  emissive = 0x000000,
) {
  return new THREE.MeshStandardMaterial({
    color,
    roughness,
    metalness,
    emissive,
    flatShading: true,
  });
}

function mesh(geometry, meshMaterial, name, shadow = true) {
  const result = new THREE.Mesh(geometry, meshMaterial);
  result.name = name;
  result.castShadow = shadow;
  result.receiveShadow = shadow;
  return result;
}

export class ModelFactory {
  constructor() {
    this.geometries = {
      head: new THREE.IcosahedronGeometry(0.34, 1),
      torso: new THREE.CapsuleGeometry(0.46, 0.72, 3, 7),
      upperLimb: new THREE.CapsuleGeometry(0.13, 0.45, 2, 6),
      lowerLimb: new THREE.CapsuleGeometry(0.11, 0.43, 2, 6),
      hand: new THREE.SphereGeometry(0.14, 7, 5),
      foot: new THREE.BoxGeometry(0.24, 0.18, 0.42),
      shoulder: new THREE.ConeGeometry(0.31, 0.32, 5),
      cape: new THREE.PlaneGeometry(0.92, 1.25, 2, 3),
      sword: new THREE.BoxGeometry(0.1, 1.15, 0.12),
      axe: new THREE.CylinderGeometry(0.06, 0.07, 1.35, 6),
      shield: new THREE.CylinderGeometry(0.48, 0.48, 0.12, 8),
      bow: new THREE.TorusGeometry(0.48, 0.045, 5, 12, Math.PI * 1.35),
      staff: new THREE.CylinderGeometry(0.055, 0.07, 1.55, 7),
      dagger: new THREE.ConeGeometry(0.1, 0.52, 4),
      skull: new THREE.DodecahedronGeometry(0.34, 0),
    };
    Object.values(this.geometries).forEach((geometry) => {
      geometry.userData.realmfallShared = true;
    });
  }
  humanoid(classId, appearance = {}, options = {}) {
    const definition = CLASSES[classId] ?? CLASSES.barbarian;
    const group = new THREE.Group();
    group.name = options.name ?? definition.name;
    group.userData.animations = ANIMATIONS.slice();
    group.userData.animation = "idle";
    group.userData.collision = {
      type: "capsule",
      radius: 0.48,
      halfHeight: 0.86,
    };
    group.userData.attachments = {
      weapon: "right-hand",
      offHand: "left-hand",
      effect: "chest",
      overhead: "marker",
    };
    const skin = material(appearance.skin ?? "#d9a978", 0.92);
    const cloth = material(appearance.accent ?? definition.color, 0.74);
    const dark = material(options.enemy ? "#263332" : "#28343a", 0.68, 0.12);
    const hair = material(appearance.hairColor ?? "#3a2923", 0.9);
    const metal = material(
      options.enemy ? (options.color ?? "#73808a") : "#9aa8a4",
      0.33,
      0.68,
    );
    const root = new THREE.Group();
    root.name = "body-root";
    group.add(root);
    const torso = mesh(
      this.geometries.torso,
      options.enemy ? dark : cloth,
      "torso",
    );
    torso.position.y = 1.37;
    torso.scale.set(options.width ?? 1, 1, options.width ?? 1);
    root.add(torso);
    const head = mesh(
      options.enemy && classId === "necromancer"
        ? this.geometries.skull
        : this.geometries.head,
      skin,
      "head",
    );
    head.position.y = 2.28;
    root.add(head);
    if (appearance.hair !== "Shaven" && !options.skeleton) {
      const hairMesh = mesh(
        new THREE.SphereGeometry(0.35, 7, 5, 0, Math.PI * 2, 0, Math.PI * 0.54),
        hair,
        "hair",
      );
      hairMesh.position.y = 2.39;
      root.add(hairMesh);
    }
    const limbs = {};
    for (const side of [-1, 1]) {
      const prefix = side < 0 ? "left" : "right";
      const armPivot = new THREE.Group();
      armPivot.name = `${prefix}-arm`;
      armPivot.position.set(side * 0.55, 1.78, 0);
      root.add(armPivot);
      const upper = mesh(
        this.geometries.upperLimb,
        cloth,
        `${prefix}-upper-arm`,
      );
      upper.position.y = -0.28;
      armPivot.add(upper);
      const forearmPivot = new THREE.Group();
      forearmPivot.name = `${prefix}-forearm`;
      forearmPivot.position.y = -0.58;
      armPivot.add(forearmPivot);
      const lower = mesh(
        this.geometries.lowerLimb,
        skin,
        `${prefix}-lower-arm`,
      );
      lower.position.y = -0.25;
      forearmPivot.add(lower);
      const hand = mesh(this.geometries.hand, skin, `${prefix}-hand`);
      hand.position.y = -0.53;
      forearmPivot.add(hand);
      const legPivot = new THREE.Group();
      legPivot.name = `${prefix}-leg`;
      legPivot.position.set(side * 0.25, 0.92, 0);
      root.add(legPivot);
      const upperLeg = mesh(
        this.geometries.upperLimb,
        dark,
        `${prefix}-upper-leg`,
      );
      upperLeg.position.y = -0.3;
      legPivot.add(upperLeg);
      const shinPivot = new THREE.Group();
      shinPivot.name = `${prefix}-shin`;
      shinPivot.position.y = -0.62;
      legPivot.add(shinPivot);
      const shin = mesh(this.geometries.lowerLimb, dark, `${prefix}-lower-leg`);
      shin.position.y = -0.25;
      shinPivot.add(shin);
      const foot = mesh(this.geometries.foot, dark, `${prefix}-foot`);
      foot.position.set(0, -0.52, 0.1);
      shinPivot.add(foot);
      limbs[`${prefix}Arm`] = armPivot;
      limbs[`${prefix}Forearm`] = forearmPivot;
      limbs[`${prefix}Leg`] = legPivot;
      limbs[`${prefix}Shin`] = shinPivot;
      if (!options.enemy && ["barbarian", "paladin"].includes(classId)) {
        const shoulder = mesh(
          this.geometries.shoulder,
          metal,
          `${prefix}-pauldron`,
        );
        shoulder.position.set(side * 0.58, 1.94, 0);
        shoulder.rotation.z = side * -0.35;
        root.add(shoulder);
      }
    }
    if (!options.enemy && appearance.cape) {
      const cape = mesh(this.geometries.cape, cloth, "cape", false);
      cape.position.set(0, 1.43, -0.48);
      cape.rotation.x = -0.1;
      root.add(cape);
      group.userData.cape = cape;
    }
    if (!options.enemy && appearance.helmet) {
      const helmet = mesh(
        new THREE.ConeGeometry(0.42, 0.62, classId === "paladin" ? 8 : 6),
        metal,
        "helmet",
      );
      helmet.position.y = 2.52;
      root.add(helmet);
    }
    const weapon = this.weapon(classId, options.enemy ? metal : cloth);
    weapon.name = "weapon";
    limbs.rightForearm.add(weapon);
    weapon.position.set(0, -0.72, 0.18);
    weapon.rotation.z = classId === "ranger" ? Math.PI / 2 : -0.18;
    if (classId === "paladin") {
      const shield = mesh(this.geometries.shield, metal, "off-hand");
      shield.rotation.z = Math.PI / 2;
      shield.position.set(0, -0.52, 0.3);
      limbs.leftForearm.add(shield);
    }
    const marker = mesh(
      new THREE.RingGeometry(0.58, 0.7, 24),
      new THREE.MeshBasicMaterial({
        color: options.marker ?? definition.color,
        transparent: true,
        opacity: 0.72,
        side: THREE.DoubleSide,
      }),
      "marker",
      false,
    );
    marker.rotation.x = -Math.PI / 2;
    marker.position.y = 0.035;
    group.add(marker);
    group.userData.parts = { root, torso, head, ...limbs, marker };
    return group;
  }
  weapon(classId, tint) {
    if (classId === "ranger") return mesh(this.geometries.bow, tint, "bow");
    if (classId === "arcanist" || classId === "necromancer") {
      const staff = mesh(this.geometries.staff, tint, "staff");
      const gem = mesh(
        new THREE.OctahedronGeometry(0.16),
        material(CLASSES[classId].color, 0.2, 0.1, CLASSES[classId].color),
        "focus-gem",
      );
      gem.position.y = -0.78;
      staff.add(gem);
      return staff;
    }
    if (classId === "rogue")
      return mesh(this.geometries.dagger, tint, "dagger");
    if (classId === "barbarian") {
      const axe = mesh(this.geometries.axe, tint, "axe");
      const blade = mesh(
        new THREE.ConeGeometry(0.32, 0.6, 3),
        tint,
        "axe-blade",
      );
      blade.position.set(0, -0.58, 0);
      blade.rotation.z = Math.PI / 2;
      axe.add(blade);
      return axe;
    }
    return mesh(this.geometries.sword, tint, "mace");
  }
  enemy(definition, options = {}) {
    const roleClass =
      definition.role === "ranged"
        ? "ranger"
        : definition.role === "artillery" || definition.role === "controller"
          ? "arcanist"
          : definition.faction === "Restless Dead"
            ? "necromancer"
            : "barbarian";
    const group = this.humanoid(
      roleClass,
      {
        skin: definition.faction === "Restless Dead" ? "#c9c3a2" : "#9a755c",
        accent: definition.color,
        hair: "Shaven",
      },
      {
        enemy: true,
        skeleton: definition.faction === "Restless Dead",
        color: definition.color,
        width: definition.role === "tank" ? 1.35 : 0.92,
        marker: options.eliteColor ?? "#c8545a",
        name: definition.name,
      },
    );
    if (options.eliteColor) {
      const crown = mesh(
        new THREE.TorusGeometry(0.36, 0.05, 5, 10),
        material(options.eliteColor, 0.25, 0.2, options.eliteColor),
        "elite-crown",
      );
      crown.position.y = 2.9;
      crown.rotation.x = Math.PI / 2;
      group.add(crown);
    }
    return group;
  }
  dispose() {
    Object.values(this.geometries).forEach((geometry) => geometry.dispose());
  }
}

export class ThreeRenderer {
  constructor(canvas, settings, assetBase) {
    this.canvas = canvas;
    this.settings = settings;
    this.assetBase = assetBase;
    this.disposed = false;
    this.quality = QUALITY[settings.quality] ?? QUALITY.medium;
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: settings.quality !== "low",
      alpha: false,
      powerPreference:
        settings.quality === "ultra" ? "high-performance" : "default",
    });
    this.renderer.setPixelRatio(
      Math.min(devicePixelRatio || 1, this.quality.ratio),
    );
    this.renderer.shadowMap.enabled = this.quality.shadows;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.05;
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x132927);
    this.scene.fog = new THREE.FogExp2(0x16302d, 0.018);
    this.camera = new THREE.PerspectiveCamera(43, 1, 0.1, 180);
    this.cameraTarget = new THREE.Vector3();
    this.cameraAngle = Math.PI * 0.25;
    this.cameraZoom = settings.cameraZoom;
    this.factory = new ModelFactory();
    this.playerModel = null;
    this.companionModel = null;
    this.enemyModels = new Map();
    this.projectileModels = new Map();
    this.effectModels = new Map();
    this.summonModels = new Map();
    this.interactableModels = new Map();
    this.raycaster = new THREE.Raycaster();
    this.pointer = new THREE.Vector2();
    this.groundPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
    this.worldGroup = new THREE.Group();
    this.entityGroup = new THREE.Group();
    this.scene.add(this.worldGroup, this.entityGroup);
    this.textureCache = new Map();
    this.shared = { ground: null };
    this.lastZone = null;
    this.lastDungeonSeed = null;
    this.lastDungeon = null;
    this.lastFrameTime = performance.now();
    this.presentationTime = 0;
    this.fps = 0;
    this.frames = 0;
    this.fpsAccumulator = 0;
    this.contextLost = false;
    this.onContextLost = null;
    this.onContextRestored = null;
    this.contextLostHandler = (event) => {
      event.preventDefault();
      this.contextLost = true;
      this.onContextLost?.();
    };
    this.contextRestoreHandler = () => {
      this.contextLost = false;
      this.onContextRestored?.();
    };
    canvas.addEventListener("webglcontextlost", this.contextLostHandler);
    canvas.addEventListener("webglcontextrestored", this.contextRestoreHandler);
    this.installLights();
    this.resize();
  }
  installLights() {
    const hemisphere = new THREE.HemisphereLight(0xb9e8dd, 0x233527, 2.25);
    this.scene.add(hemisphere);
    this.sun = new THREE.DirectionalLight(0xffe7bd, 3.1);
    this.sun.position.set(-18, 28, 15);
    this.sun.castShadow = this.quality.shadows;
    this.sun.shadow.mapSize.set(this.quality.shadowMap, this.quality.shadowMap);
    this.sun.shadow.camera.left = -26;
    this.sun.shadow.camera.right = 26;
    this.sun.shadow.camera.top = 26;
    this.sun.shadow.camera.bottom = -26;
    this.sun.shadow.bias = -0.0007;
    this.scene.add(this.sun);
    const rim = new THREE.DirectionalLight(0x6ac8bd, 1.2);
    rim.position.set(15, 10, -18);
    this.scene.add(rim);
  }
  loadTexture(path) {
    if (this.textureCache.has(path)) return this.textureCache.get(path);
    const texture = new THREE.TextureLoader().load(
      `${this.assetBase}${path}`,
      (loaded) => {
        if (this.disposed) loaded.dispose();
      },
    );
    texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
    texture.repeat.set(8, 8);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.anisotropy = Math.min(
      8,
      this.renderer.capabilities.getMaxAnisotropy(),
    );
    texture.userData.realmfallShared = true;
    this.textureCache.set(path, texture);
    return texture;
  }
  buildWorld(zoneId, dungeon = null) {
    while (this.worldGroup.children.length) {
      const child = this.worldGroup.children[0];
      this.worldGroup.remove(child);
      disposeObject(child);
    }
    const region = REGIONS.find((entry) => entry.id === zoneId) ?? REGIONS[1];
    this.lastZone = zoneId;
    this.lastDungeonSeed = dungeon?.seed ?? null;
    this.lastDungeon = dungeon;
    const palette = {
      sanctuary: [0x416c5c, 0x6b8f77, 0x789081],
      grass: [0x375d38, 0x557341, 0x6a5940],
      ash: [0x4b302b, 0x6a3c2c, 0x913e2c],
      marsh: [0x243f3d, 0x3d5850, 0x4c5962],
      ice: [0x587c82, 0x83a9ac, 0x7796ad],
      hollow: [0x342f40, 0x5a4354, 0x6e586f],
    }[region.terrain];
    this.scene.background.set(palette[0]);
    this.scene.fog.color.set(palette[0]);
    const textureName = `terrain-${region.terrain}.png`;
    const groundMaterial = new THREE.MeshStandardMaterial({
      color: palette[1],
      map: this.loadTexture(textureName),
      roughness: 0.93,
      metalness: 0.02,
    });
    const ground = mesh(
      new THREE.PlaneGeometry(WORLD_SIZE, WORLD_SIZE, 32, 32),
      groundMaterial,
      "world-ground",
    );
    ground.rotation.x = -Math.PI / 2;
    ground.receiveShadow = true;
    this.worldGroup.add(ground);
    const ring = mesh(
      new THREE.RingGeometry(WORLD_SIZE * 0.47, WORLD_SIZE * 0.5, 64),
      material(palette[2], 0.9),
      "world-boundary",
    );
    ring.rotation.x = -Math.PI / 2;
    ring.position.y = 0.03;
    this.worldGroup.add(ring);
    if (dungeon) this.buildDungeon(dungeon, palette);
    else {
      this.buildInstancedProps(region, palette);
      this.buildLandmarks(region, palette);
    }
  }
  buildDungeon(dungeon, palette) {
    const wallCells = [];
    for (let y = 0; y < dungeon.height; y += 1)
      for (let x = 0; x < dungeon.width; x += 1) {
        if (dungeon.tiles[y][x] === 1) continue;
        const bordersFloor = [
          [x + 1, y],
          [x - 1, y],
          [x, y + 1],
          [x, y - 1],
        ].some(([nextX, nextY]) => dungeon.tiles[nextY]?.[nextX] === 1);
        if (bordersFloor) wallCells.push({ x, y });
      }
    const walls = new THREE.InstancedMesh(
      new THREE.BoxGeometry(1, 2.7, 1),
      material(palette[2], 0.84),
      wallCells.length,
    );
    walls.name = "dungeon-instanced-walls";
    walls.castShadow = this.quality.shadows;
    walls.receiveShadow = true;
    const matrix = new THREE.Matrix4();
    wallCells.forEach((cell, index) => {
      const height = 2.2 + ((cell.x * 17 + cell.y * 31) % 5) * 0.18;
      matrix.compose(
        new THREE.Vector3(
          cell.x - dungeon.width / 2 + 0.5,
          height / 2,
          cell.y - dungeon.height / 2 + 0.5,
        ),
        new THREE.Quaternion(),
        new THREE.Vector3(1, height / 2.7, 1),
      );
      walls.setMatrixAt(index, matrix);
    });
    walls.instanceMatrix.needsUpdate = true;
    this.worldGroup.add(walls);
    for (const room of dungeon.rooms) {
      if (!["boss", "treasure", "shrine", "secret"].includes(room.type))
        continue;
      const color =
        room.type === "boss"
          ? 0xe05b69
          : room.type === "secret"
            ? 0xb986dc
            : 0xe7ca74;
      const marker = mesh(
        new THREE.RingGeometry(0.8, 1.05, 20),
        new THREE.MeshBasicMaterial({
          color,
          transparent: true,
          opacity: 0.5,
          side: THREE.DoubleSide,
        }),
        `dungeon-${room.type}`,
        false,
      );
      marker.rotation.x = -Math.PI / 2;
      marker.position.set(
        room.x + room.w / 2 - dungeon.width / 2,
        0.04,
        room.y + room.h / 2 - dungeon.height / 2,
      );
      marker.scale.setScalar(room.type === "boss" ? 2.4 : 1.2);
      this.worldGroup.add(marker);
    }
  }
  buildInstancedProps(region, palette) {
    const count = this.quality.vegetation;
    const geometry =
      region.terrain === "ice" || region.terrain === "hollow"
        ? new THREE.ConeGeometry(0.6, 2.8, 5)
        : new THREE.ConeGeometry(0.72, 2.5, 7);
    const props = new THREE.InstancedMesh(
      geometry,
      material(palette[2], 0.9),
      count,
    );
    props.name = "instanced-foliage";
    props.castShadow = this.quality.shadows;
    props.receiveShadow = true;
    const matrix = new THREE.Matrix4();
    const random = this.hash(region.id);
    for (let index = 0; index < count; index += 1) {
      const angle = random(index) * Math.PI * 2;
      const radius = 12 + random(index + 800) * 18;
      const scale = 0.65 + random(index + 1600) * 1.15;
      matrix.compose(
        new THREE.Vector3(
          Math.cos(angle) * radius,
          1.15 * scale,
          Math.sin(angle) * radius,
        ),
        new THREE.Quaternion().setFromAxisAngle(
          new THREE.Vector3(0, 1, 0),
          random(index + 2200) * Math.PI,
        ),
        new THREE.Vector3(scale, scale, scale),
      );
      props.setMatrixAt(index, matrix);
    }
    props.instanceMatrix.needsUpdate = true;
    this.worldGroup.add(props);
  }
  buildLandmarks(region, palette) {
    const stone = material(palette[2], 0.82);
    const wood = material(0x594232, 0.92);
    const crystal = material(region.color, 0.24, 0.28, region.color);
    for (let index = 0; index < 8; index += 1) {
      const angle = (index * Math.PI) / 4;
      const radius = index % 2 ? 19 : 25;
      const ruin = new THREE.Group();
      ruin.position.set(Math.cos(angle) * radius, 0, Math.sin(angle) * radius);
      ruin.rotation.y = -angle;
      const pillarA = mesh(
        new THREE.BoxGeometry(1.1, 4 + (index % 3), 1.1),
        stone,
        "ruin-pillar",
      );
      pillarA.position.set(-2, pillarA.geometry.parameters.height / 2, 0);
      const pillarB = pillarA.clone();
      pillarB.position.x = 2;
      const beam = mesh(new THREE.BoxGeometry(5, 0.8, 1.1), stone, "ruin-beam");
      beam.position.y = 4;
      ruin.add(pillarA, pillarB, beam);
      this.worldGroup.add(ruin);
    }
    if (region.id === "veilwatch") {
      const sanctuary = new THREE.Group();
      for (let index = 0; index < 5; index += 1) {
        const tower = mesh(
          new THREE.CylinderGeometry(1.7, 2.1, 5.5, 8),
          stone,
          "sanctuary-tower",
        );
        tower.position.set((index - 2) * 6, 2.75, -17);
        const roof = mesh(
          new THREE.ConeGeometry(2.25, 2.8, 8),
          material(0x315d56, 0.75, 0.1),
          "tower-roof",
        );
        roof.position.set(tower.position.x, 6.9, -17);
        sanctuary.add(tower, roof);
      }
      const gate = mesh(
        new THREE.TorusGeometry(3, 0.35, 8, 20, Math.PI),
        crystal,
        "portal-gate",
      );
      gate.position.set(0, 3, -8);
      sanctuary.add(gate);
      this.worldGroup.add(sanctuary);
    } else {
      for (let index = 0; index < 5; index += 1) {
        const shard = mesh(
          new THREE.OctahedronGeometry(0.65 + index * 0.12, 0),
          crystal,
          "crown-shard",
        );
        shard.position.set(-8 + index * 4, 1.2 + (index % 2), -12);
        shard.rotation.z = 0.25 * index;
        this.worldGroup.add(shard);
      }
    }
    const bridge = mesh(new THREE.BoxGeometry(6, 0.4, 12), wood, "bridge");
    bridge.position.set(-13, 0.3, 6);
    this.worldGroup.add(bridge);
  }
  hash(seed) {
    let base = 0;
    for (const character of seed)
      base = Math.imul(base ^ character.charCodeAt(0), 2654435761);
    return (index) => {
      let value = Math.imul(base ^ index, 2246822519);
      value ^= value >>> 13;
      return (value >>> 0) / 4294967295;
    };
  }
  setPlayer(character) {
    if (this.playerModel) {
      this.entityGroup.remove(this.playerModel);
      disposeObject(this.playerModel);
    }
    this.playerModel = this.factory.humanoid(
      character.classId,
      character.appearance,
    );
    this.playerModel.userData.entityId = character.id;
    this.entityGroup.add(this.playerModel);
  }
  ensureEnemy(entity) {
    if (this.enemyModels.has(entity.id)) return this.enemyModels.get(entity.id);
    const definition =
      ENEMIES.find((entry) => entry.id === entity.definitionId) ?? ENEMIES[0];
    const model = this.factory.enemy(definition, {
      eliteColor: entity.elite?.color,
    });
    model.userData.entityId = entity.id;
    if (entity.bossId) model.scale.setScalar(1.75);
    else if (entity.miniboss) model.scale.setScalar(1.35);
    this.enemyModels.set(entity.id, model);
    this.entityGroup.add(model);
    return model;
  }
  ensureProjectile(projectile) {
    if (this.projectileModels.has(projectile.id))
      return this.projectileModels.get(projectile.id);
    const model = mesh(
      new THREE.OctahedronGeometry(0.23),
      material(projectile.color, 0.18, 0.15, projectile.color),
      "projectile",
      false,
    );
    model.userData.entityId = projectile.id;
    this.projectileModels.set(projectile.id, model);
    this.entityGroup.add(model);
    return model;
  }
  ensureEffect(effect) {
    if (this.effectModels.has(effect.id))
      return this.effectModels.get(effect.id);
    const ring = mesh(
      new THREE.RingGeometry(0.6, 0.8, 28),
      new THREE.MeshBasicMaterial({
        color: effect.color,
        transparent: true,
        opacity: 0.75,
        side: THREE.DoubleSide,
        depthWrite: false,
      }),
      "ability-effect",
      false,
    );
    ring.rotation.x = -Math.PI / 2;
    this.effectModels.set(effect.id, ring);
    this.entityGroup.add(ring);
    return ring;
  }
  ensureInteractable(entity) {
    if (this.interactableModels.has(entity.id))
      return this.interactableModels.get(entity.id);
    const color = entity.type === "checkpoint" ? 0x75e1bf : 0x9c81e8;
    const group = new THREE.Group();
    const base = mesh(
      new THREE.CylinderGeometry(0.72, 1, 0.34, 8),
      material(0x4b625a, 0.8),
      "interactable-base",
    );
    base.position.y = 0.17;
    group.add(base);
    const rune = mesh(
      new THREE.OctahedronGeometry(0.45),
      material(color, 0.2, 0.18, color),
      "interactable-rune",
    );
    rune.position.y = 1.15;
    group.add(rune);
    group.userData.rune = rune;
    this.interactableModels.set(entity.id, group);
    this.entityGroup.add(group);
    return group;
  }
  animateModel(model, animation, time, speed = 1) {
    const parts = model.userData.parts;
    if (!parts) return;
    if (model.userData.animation !== animation) {
      model.userData.animation = animation;
      model.userData.animationStartedAt = time;
    }
    const elapsed = Math.max(
      0,
      time - (model.userData.animationStartedAt ?? time),
    );
    const cycle = time * speed;
    const bob = Math.sin(cycle * 5) * 0.035;
    parts.root.position.y = bob;
    parts.leftArm.rotation.set(0, 0, 0);
    parts.rightArm.rotation.set(0, 0, 0);
    parts.leftLeg.rotation.set(0, 0, 0);
    parts.rightLeg.rotation.set(0, 0, 0);
    parts.torso.rotation.set(0, 0, 0);
    parts.root.rotation.set(0, 0, 0);
    parts.root.scale.setScalar(1);
    if (
      animation === "walk" ||
      animation === "run" ||
      animation === "pursue" ||
      animation === "reposition"
    ) {
      const amplitude = animation === "run" ? 0.8 : 0.52;
      const phase = Math.sin(cycle * (animation === "run" ? 10 : 7));
      parts.leftLeg.rotation.x = phase * amplitude;
      parts.rightLeg.rotation.x = -phase * amplitude;
      parts.leftArm.rotation.x = -phase * amplitude * 0.7;
      parts.rightArm.rotation.x = phase * amplitude * 0.7;
    } else if (["cast", "channel"].includes(animation)) {
      parts.leftArm.rotation.x = -1.8;
      parts.rightArm.rotation.x = -1.8;
      parts.leftArm.rotation.z = -0.35;
      parts.rightArm.rotation.z = 0.35;
    } else if (
      ["basic-attack", "attack", "secondary-attack"].includes(animation)
    ) {
      parts.rightArm.rotation.x = -1.2 - Math.sin(cycle * 15) * 0.85;
      parts.torso.rotation.y = Math.sin(cycle * 12) * 0.3;
    } else if (animation === "dodge") {
      parts.root.rotation.x = Math.sin(cycle * 16) * 0.55;
    } else if (animation === "block") {
      parts.leftArm.rotation.x = -1.5;
      parts.leftArm.rotation.z = -0.6;
    } else if (animation === "hit") {
      parts.torso.rotation.x = -0.25;
    } else if (["stun", "stunned"].includes(animation)) {
      parts.head.rotation.z = Math.sin(cycle * 7) * 0.18;
    } else if (animation === "death") {
      parts.root.rotation.z = Math.min(Math.PI / 2, elapsed * 2.8);
      parts.root.position.y = 0.08;
    } else if (animation === "respawn") {
      parts.root.scale.setScalar(Math.min(1, 0.1 + elapsed * 3.4));
    } else if (animation === "victory") {
      parts.rightArm.rotation.x = -2.8;
      parts.leftArm.rotation.x = -2.8;
    } else if (animation === "interaction") {
      parts.rightForearm.rotation.x = -1.2;
    }
    if (model.userData.cape)
      model.userData.cape.rotation.x =
        -0.12 + Math.sin(cycle * 3) * 0.05 + (animation === "run" ? -0.18 : 0);
  }
  sync(runtime, alpha, now) {
    if (!runtime) return;
    const renderDelta = Math.min(
      0.1,
      Math.max(0.001, (now - this.lastFrameTime) / 1000),
    );
    this.frameTimeMs = renderDelta * 1000;
    this.lastFrameTime = now;
    if (!runtime.paused || runtime.gameState === "dead")
      this.presentationTime += renderDelta;
    if (
      this.lastZone !== runtime.zoneId ||
      this.lastDungeonSeed !== (runtime.dungeon?.seed ?? null)
    )
      this.buildWorld(runtime.zoneId, runtime.dungeon);
    if (
      !this.playerModel ||
      this.playerModel.userData.entityId !== runtime.player.id
    )
      this.setPlayer(runtime.character);
    const player = runtime.player;
    this.playerModel.position.set(
      player.position.x,
      player.position.y,
      player.position.z,
    );
    this.playerModel.rotation.y = Math.atan2(player.facing.x, player.facing.z);
    this.animateModel(
      this.playerModel,
      player.animation,
      this.presentationTime,
      1,
    );
    if (runtime.companion?.alive) {
      if (
        !this.companionModel ||
        this.companionModel.userData.entityId !== runtime.companion.id
      ) {
        if (this.companionModel) {
          this.entityGroup.remove(this.companionModel);
          disposeObject(this.companionModel);
        }
        const companion = COMPANIONS.find(
          (entry) => entry.id === runtime.companion.definitionId,
        );
        const classId =
          companion?.role === "tank"
            ? "paladin"
            : companion?.role === "support"
              ? "arcanist"
              : "ranger";
        this.companionModel = this.factory.humanoid(
          classId,
          {
            accent: companion?.color ?? "#7ce5bd",
            skin: "#c9936f",
            hair: "Cropped",
            cape: true,
          },
          { name: companion?.name ?? "Warden Companion" },
        );
        this.companionModel.userData.entityId = runtime.companion.id;
        this.companionModel.scale.setScalar(0.9);
        this.entityGroup.add(this.companionModel);
      }
      this.companionModel.position.set(
        runtime.companion.position.x,
        0,
        runtime.companion.position.z,
      );
      this.animateModel(
        this.companionModel,
        runtime.companion.state === "attack" ? "basic-attack" : "run",
        this.presentationTime,
        0.9,
      );
    } else if (this.companionModel) {
      this.entityGroup.remove(this.companionModel);
      disposeObject(this.companionModel);
      this.companionModel = null;
    }
    const summonIds = new Set();
    for (const summon of runtime.summons ?? []) {
      summonIds.add(summon.id);
      let model = this.summonModels.get(summon.id);
      if (!model) {
        const definition =
          ENEMIES.find((entry) => entry.faction === "Restless Dead") ??
          ENEMIES[0];
        model = this.factory.enemy(definition, { eliteColor: "#73d4c1" });
        model.userData.entityId = summon.id;
        model.scale.setScalar(0.68);
        this.summonModels.set(summon.id, model);
        this.entityGroup.add(model);
      }
      model.position.set(summon.position.x, 0, summon.position.z);
      this.animateModel(
        model,
        summon.state === "attack" ? "basic-attack" : "run",
        this.presentationTime,
        1.1,
      );
    }
    for (const [id, model] of this.summonModels)
      if (!summonIds.has(id)) {
        this.entityGroup.remove(model);
        disposeObject(model);
        this.summonModels.delete(id);
      }
    const liveEnemyIds = new Set();
    for (const enemy of runtime.enemies) {
      liveEnemyIds.add(enemy.id);
      const model = this.ensureEnemy(enemy);
      model.position.set(enemy.position.x, 0, enemy.position.z);
      model.rotation.y = Math.atan2(
        enemy.facing?.x ?? 0,
        enemy.facing?.z ?? -1,
      );
      this.animateModel(
        model,
        enemy.alive ? enemy.state : "death",
        this.presentationTime + enemy.id.length * 0.1,
        enemy.speed / 3,
      );
    }
    for (const [id, model] of this.enemyModels)
      if (!liveEnemyIds.has(id)) {
        this.entityGroup.remove(model);
        disposeObject(model);
        this.enemyModels.delete(id);
      }
    const projectileIds = new Set();
    for (const projectile of runtime.projectiles) {
      projectileIds.add(projectile.id);
      const model = this.ensureProjectile(projectile);
      model.position.set(
        projectile.position.x,
        projectile.position.y,
        projectile.position.z,
      );
      model.rotation.y += 0.18;
      model.rotation.x += 0.12;
    }
    for (const [id, model] of this.projectileModels)
      if (!projectileIds.has(id)) {
        this.entityGroup.remove(model);
        disposeObject(model);
        this.projectileModels.delete(id);
      }
    const effectIds = new Set();
    for (const effect of runtime.effects.slice(-this.quality.particles)) {
      effectIds.add(effect.id);
      const model = this.ensureEffect(effect);
      model.position.set(effect.position.x, 0.06, effect.position.z);
      const progress = 1 - effect.remaining / 0.65;
      model.scale.setScalar(effect.radius * (0.4 + progress));
      model.material.opacity = Math.max(0, effect.remaining / 0.65);
    }
    for (const [id, model] of this.effectModels)
      if (!effectIds.has(id)) {
        this.entityGroup.remove(model);
        disposeObject(model);
        this.effectModels.delete(id);
      }
    const interactableIds = new Set();
    for (const entity of runtime.interactables) {
      interactableIds.add(entity.id);
      const model = this.ensureInteractable(entity);
      model.position.set(entity.position.x, 0, entity.position.z);
      model.userData.rune.rotation.y += 0.012;
      model.userData.rune.position.y = 1.15 + Math.sin(runtime.time * 2) * 0.12;
      if (entity.active) model.userData.rune.scale.setScalar(1.25);
    }
    for (const [id, model] of this.interactableModels)
      if (!interactableIds.has(id)) {
        this.entityGroup.remove(model);
        disposeObject(model);
        this.interactableModels.delete(id);
      }
    const targetX = player.position.x,
      targetZ = player.position.z;
    const smooth = this.settings.reducedMotion
      ? 1
      : 1 - Math.exp(-9 * renderDelta);
    this.cameraTarget.x += (targetX - this.cameraTarget.x) * smooth;
    this.cameraTarget.z += (targetZ - this.cameraTarget.z) * smooth;
    const distance = this.cameraZoom;
    const horizontal = distance * 0.74;
    this.camera.position.set(
      this.cameraTarget.x + Math.cos(this.cameraAngle) * horizontal,
      distance * 0.75,
      this.cameraTarget.z + Math.sin(this.cameraAngle) * horizontal,
    );
    this.camera.lookAt(this.cameraTarget.x, 0.7, this.cameraTarget.z);
    if (!this.contextLost) this.renderer.render(this.scene, this.camera);
    this.frames += 1;
    this.fpsAccumulator += renderDelta;
    if (this.fpsAccumulator >= 0.5) {
      this.fps = Math.round(this.frames / this.fpsAccumulator);
      this.frames = 0;
      this.fpsAccumulator = 0;
    }
  }
  screenToWorld(clientX, clientY) {
    const rect = this.canvas.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return null;
    this.pointer.set(
      ((clientX - rect.left) / rect.width) * 2 - 1,
      -((clientY - rect.top) / rect.height) * 2 + 1,
    );
    this.raycaster.setFromCamera(this.pointer, this.camera);
    const point = new THREE.Vector3();
    return this.raycaster.ray.intersectPlane(this.groundPlane, point)
      ? { x: point.x, y: 0, z: point.z }
      : null;
  }
  enemyAt(clientX, clientY, runtime) {
    const rect = this.canvas.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return null;
    this.pointer.set(
      ((clientX - rect.left) / rect.width) * 2 - 1,
      -((clientY - rect.top) / rect.height) * 2 + 1,
    );
    this.raycaster.setFromCamera(this.pointer, this.camera);
    const hits = this.raycaster.intersectObjects(
      [...this.enemyModels.values()],
      true,
    );
    for (const hit of hits) {
      let object = hit.object;
      while (object && !object.userData.entityId) object = object.parent;
      const entity = runtime.enemies.find(
        (entry) => entry.id === object?.userData.entityId && entry.alive,
      );
      if (entity) return entity;
    }
    return null;
  }
  rotate(delta) {
    if (!this.settings.fixedCamera) this.cameraAngle += delta;
  }
  zoom(delta) {
    this.cameraZoom = Math.max(14, Math.min(34, this.cameraZoom + delta));
    this.settings.cameraZoom = this.cameraZoom;
  }
  setQuality(name) {
    const next = QUALITY[name] ?? QUALITY.medium;
    const changed = this.quality !== next;
    this.quality = next;
    this.renderer.setPixelRatio(
      Math.min(devicePixelRatio || 1, this.quality.ratio),
    );
    this.renderer.shadowMap.enabled = this.quality.shadows;
    this.sun.castShadow = this.quality.shadows;
    this.sun.shadow.mapSize.set(this.quality.shadowMap, this.quality.shadowMap);
    if (changed && this.sun.shadow.map) {
      this.sun.shadow.map.dispose();
      this.sun.shadow.map = null;
    }
    if (changed && this.lastZone)
      this.buildWorld(this.lastZone, this.lastDungeon);
    this.resize();
  }
  resize() {
    const width = Math.max(1, this.canvas.clientWidth),
      height = Math.max(1, this.canvas.clientHeight);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height, false);
  }
  diagnostics() {
    return {
      fps: this.fps,
      frameTimeMs: this.frameTimeMs ?? 0,
      drawCalls: this.renderer.info.render.calls,
      triangles: this.renderer.info.render.triangles,
      textures: this.renderer.info.memory.textures,
      geometries: this.renderer.info.memory.geometries,
      renderTargets: 0,
      programs: this.renderer.info.programs?.length ?? 0,
      contextLost: this.contextLost,
      maxTextureSize: this.renderer.capabilities.maxTextureSize,
      WebGL2: this.renderer.capabilities.isWebGL2,
    };
  }
  teardown({ loseContext = true } = {}) {
    if (!this.renderer) return;
    this.disposed = true;
    this.canvas.removeEventListener(
      "webglcontextlost",
      this.contextLostHandler,
    );
    this.canvas.removeEventListener(
      "webglcontextrestored",
      this.contextRestoreHandler,
    );
    disposeObject(this.scene);
    this.textureCache.forEach((texture) => texture.dispose());
    this.textureCache.clear();
    this.factory.dispose();
    this.renderer.renderLists.dispose();
    this.renderer.dispose();
    if (loseContext) this.renderer.forceContextLoss();
    this.enemyModels.clear();
    this.projectileModels.clear();
    this.effectModels.clear();
    this.summonModels.clear();
    this.interactableModels.clear();
    this.playerModel = null;
    this.companionModel = null;
    this.renderer = null;
  }
}

export function drawMinimap(canvas, runtime) {
  const context = canvas.getContext("2d");
  const width = canvas.width,
    height = canvas.height;
  context.clearRect(0, 0, width, height);
  const region =
    REGIONS.find((entry) => entry.id === runtime.zoneId) ?? REGIONS[1];
  context.fillStyle = "#0a211f";
  context.fillRect(0, 0, width, height);
  context.strokeStyle = region.color;
  context.globalAlpha = 0.35;
  context.lineWidth = 2;
  for (let radius = 18; radius < width; radius += 24) {
    context.beginPath();
    context.arc(width / 2, height / 2, radius, 0, Math.PI * 2);
    context.stroke();
  }
  context.globalAlpha = 0.8;
  const scale = width / 44;
  const project = (position) => ({
    x: width / 2 + (position.x - runtime.player.position.x) * scale,
    y: height / 2 + (position.z - runtime.player.position.z) * scale,
  });
  for (const entity of runtime.interactables) {
    const point = project(entity.position);
    context.fillStyle = entity.type === "checkpoint" ? "#74e2bd" : "#b18be6";
    context.fillRect(point.x - 3, point.y - 3, 6, 6);
  }
  for (const enemy of runtime.enemies)
    if (
      enemy.alive &&
      Math.hypot(
        enemy.position.x - runtime.player.position.x,
        enemy.position.z - runtime.player.position.z,
      ) < 20
    ) {
      const point = project(enemy.position);
      context.fillStyle = enemy.bossId ? "#ffcf72" : "#e56469";
      context.beginPath();
      context.arc(point.x, point.y, enemy.bossId ? 4 : 2, 0, Math.PI * 2);
      context.fill();
    }
  if (
    runtime.character.echoCache &&
    !runtime.character.echoCache.recovered &&
    runtime.character.echoCache.zoneId === runtime.zoneId
  ) {
    const point = project(runtime.character.echoCache.position);
    context.strokeStyle = "#f2d57d";
    context.strokeRect(point.x - 4, point.y - 4, 8, 8);
  }
  context.save();
  context.translate(width / 2, height / 2);
  context.rotate(-Math.atan2(runtime.player.facing.x, runtime.player.facing.z));
  context.fillStyle = "#fff";
  context.beginPath();
  context.moveTo(0, -7);
  context.lineTo(5, 6);
  context.lineTo(-5, 6);
  context.closePath();
  context.fill();
  context.restore();
  context.globalAlpha = 1;
}
